import './radio-group-buttons.scss';

import React, { PropsWithChildren, useEffect } from 'react';
import { useRadio, useRadioGroup } from 'react-aria';
import { type RadioGroupState, useRadioGroupState } from 'react-stately';

import { cn } from '../../utils/helpers';
import {
  ButtonGroupInputType,
  ButtonGroupItem,
} from '../button-group-item/button-group-item';

const RadioContext = React.createContext<RadioGroupState | null>(null);

type RadioGroupButtonsProps<TValue> = PropsWithChildren & {
  errorMessage?: string;
  label?: string;
  defaultValue?: TValue;
  listClassName?: string;
  onChange: (selectedValue: TValue) => void;
};

// Looks like VSCode has some issues with an arrow function here without adding some verbose type assertions.
// eslint-disable-next-line react/function-component-definition, prefer-arrow-functions/prefer-arrow-functions
export function RadioGroupButtons<TValue extends string = string>(
  props: RadioGroupButtonsProps<TValue>,
) {
  const { listClassName, onChange, ...rest } = props;
  const { children, errorMessage, label } = rest;
  const state = useRadioGroupState(rest);
  const { radioGroupProps, labelProps, errorMessageProps } = useRadioGroup(
    rest,
    state,
  );

  const { selectedValue } = state;
  useEffect(() => {
    onChange(selectedValue as TValue);
  }, [selectedValue]);

  return (
    <div {...radioGroupProps} className="RadioGroupButtons">
      {label && (
        <p className="RadioGroupButtons__label" {...labelProps}>
          {label}
        </p>
      )}
      <RadioContext.Provider value={state}>
        <div
          className={cn(
            `RadioGroupButtons__list gap-comfy-xs my-comfy-lg`,
            listClassName,
          )}
        >
          {/* <div className="RadioGroupButtons__list gap-comfy-xs my-comfy-lg"> */}
          {children}
        </div>
      </RadioContext.Provider>
      {errorMessage && state.isInvalid && (
        <div {...errorMessageProps} style={{ color: 'red', fontSize: 12 }}>
          {errorMessage}
        </div>
      )}
    </div>
  );
}

export type RadioGroupButtonsItemProps = PropsWithChildren<{
  value: string;
  id: string;
}>;
export const RadioGroupButtonsItem: React.FC<RadioGroupButtonsItemProps> = ({
  id,
  children,
  value,
}) => {
  return (
    <ButtonGroupItem<RadioGroupState>
      useItemHook={useRadio}
      value={value}
      id={id}
      stateContext={RadioContext}
      inputVariant={ButtonGroupInputType.Radio}
    >
      {children}
    </ButtonGroupItem>
  );
};
